import React from 'react';
import { Placeholder, VisitorIdentification } from '@sitecore-jss/sitecore-jss-react';
import deepEqual from 'deep-equal';
import Helmet from 'react-helmet';
import './assets/app.css';
import 'vitality-design-system/dist/assets/css/main.css';
import { useDispatch } from 'react-redux';
import { setAgentDetails } from './redux/slices/agentDetailsSlice';
import { setServiceDownErrorEventName } from './utils/analytics/datalayer';
import PropTypes from 'prop-types';

const importModules = async () => {
  if (typeof window !== 'undefined') {
    await import('vitality-design-system/dist/js/kit/bb0d320a6a.js');
  }
};

importModules();

const importStylesheet = async stylesheet => {
  if (!stylesheet || typeof stylesheet !== 'string') {
    return;
  }
  await import(`vitality-design-system/dist/assets/css/${stylesheet}.css`);
};

const getPageOptions = pageOptions => {
  if (!pageOptions || pageOptions.length === 0) {
    return null;
  }
  return pageOptions[0] || pageOptions;
};

/*
  APP LAYOUT
  This is where the app's HTML structure and root placeholders should be defined.

  All routes share this root layout by default (this could be customized in RouteHandler),
  but components added to inner placeholders are route-specific.
*/

const Layout = ({ route }) => {
  const dispatch = useDispatch();
  setServiceDownErrorEventName(route.fields?.serviceDownErrorEventName?.value);
  const pageOptions = getPageOptions(route.fields?.pageOptions);
  if (pageOptions) {
    const stylesheetOptions = pageOptions.fields?.stylesheet[0] || pageOptions.fields?.stylesheet;
    const cmsAgencyDetails =
      pageOptions.fields?.agencyDetails[0] || pageOptions.fields?.agencyDetails;
    if (stylesheetOptions) {
      importStylesheet(stylesheetOptions.fields?.stylesheet?.value);
    }
    if (cmsAgencyDetails) {
      const agentDetails = {
        agentName: cmsAgencyDetails.fields?.name?.value,
        agentEntityId: cmsAgencyDetails.fields?.entityNumber?.value,
        salesAgentCode: cmsAgencyDetails.fields?.code?.value,
        salesChannel: cmsAgencyDetails.fields?.salesChannel?.value,
      };
      dispatch(setAgentDetails(agentDetails));
    }
  }

  return (
    <React.Fragment>
      {/* react-helmet enables setting <head> contents, like title and OG meta tags */}
      <Helmet>
        <title>
          {(route.fields && route.fields.pageTitle && route.fields.pageTitle.value) || 'Page'}
        </title>
      </Helmet>

      {/*
      VisitorIdentification is necessary for Sitecore Analytics to determine if the visitor is a robot.
      If Sitecore XP (with xConnect/xDB) is used, this is required or else analytics will not be collected for the JSS app.
      For XM (CMS-only) apps, this should be removed.

      VI detection only runs once for a given analytics ID, so this is not a recurring operation once cookies are established.
    */}
      <VisitorIdentification />

      {/* root placeholder for the app, which we add components to using route data */}
      <Placeholder name="layout" rendering={route} />
    </React.Fragment>
  );
};

// We don't want to re-render `Layout` when route is changed but layout data is not loaded
// Layout will be re-rendered only when layout data is changed
const propsAreEqual = (prevProps, nextProps) => {
  if (deepEqual(prevProps.route, nextProps.route)) {
    return true;
  }

  return false;
};

Layout.propTypes = {
  route: PropTypes.shape({
    fields: PropTypes.shape({
      pageTitle: PropTypes.shape({ value: PropTypes.string }),
      pageOptions: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
      serviceDownErrorEventName: PropTypes.shape({ value: PropTypes.string }),
    }),
  }),
};

export default React.memo(Layout, propsAreEqual);
